import {
  getEnergyConsumerLoadsSummary,
  getPowerTransformerEndsVoltageSummary
} from '..';
import type { CompletedCalculationTactic } from '../../..';
import { AttentionStatusStorage } from '../../../evaluation/AttentionStatusRepository';
import type { ForRuleProviding } from '../../../evaluation/RulesProvider';
import { AcLineSegmentEvaluationEngine } from '../../../evaluation/loadFlow/engines/lowVoltage/acLineSegment/acLineSegmentEvaluationEngine';
import { EnergyConsumerEvaluationEngine } from '../../../evaluation/loadFlow/engines/lowVoltage/energyConsumer/energyConsumerEvaluationEngine';
import { PowerTransformerEvaluationEngine } from '../../../evaluation/loadFlow/engines/lowVoltage/powerTransformer/powerTransformerEvaluationEngine';
import type { AcLineSegmentEvaluationResult } from '../../../evaluation/loadFlow/gridElements/acLineSegment/acLineSegmentEvaluation';
import { AcLineSegmentEvaluation } from '../../../evaluation/loadFlow/gridElements/acLineSegment/acLineSegmentEvaluation';
import type { EnergyConsumerEvaluationResult } from '../../../evaluation/loadFlow/gridElements/energyConsumer/energyConsumerEvaluation';
import { EnergyConsumerEvaluation } from '../../../evaluation/loadFlow/gridElements/energyConsumer/energyConsumerEvaluation';
import type { HighestLoadingPowerTransformerEvaluationResult } from '../../../evaluation/loadFlow/gridElements/powerTransformer/highestLoadingPowerTransformerEvaluation';
import { PowerTransformerEvaluation } from '../../../evaluation/loadFlow/gridElements/powerTransformer/powerTransformerEvaluation';
import type { LvLoadFlowSummaryEvaluatedResults } from './LvLoadFlowSummaryEvaluatedResults';

export function getLVLoadFlowScenarioSummary(
  calculationTactic: CompletedCalculationTactic,
  rulesProvider: ForRuleProviding,
  scenariosIds: string[]
): LvLoadFlowSummaryEvaluatedResults {
  const loadFlowScenariosResults =
    calculationTactic.calculationSetResult.calculationScenarios.loadFlow.filter(
      f => scenariosIds.some(scenarioId => f.scenarioId === scenarioId)
    );

  const acLineSegmentEvaluationResult = AcLineSegmentEvaluation.from(
    loadFlowScenariosResults,
    AttentionStatusStorage.noStorage()
  ).evaluateSummary(new AcLineSegmentEvaluationEngine(rulesProvider));

  const energyConsumerEvaluationResult = EnergyConsumerEvaluation.from(
    loadFlowScenariosResults,
    AttentionStatusStorage.noStorage()
  ).evaluateSummary(new EnergyConsumerEvaluationEngine(rulesProvider));

  const transformerEvaluationResult = PowerTransformerEvaluation.from(
    loadFlowScenariosResults,
    AttentionStatusStorage.noStorage()
  ).evaluateOneWithHighestLoadingPercent(
    new PowerTransformerEvaluationEngine(rulesProvider)
  );

  const powerTransformers = PowerTransformerEvaluation.from(
    loadFlowScenariosResults,
    AttentionStatusStorage.noStorage()
  ).evaluateSummary(new PowerTransformerEvaluationEngine(rulesProvider));

  const powerTransformerEndsVoltageSummary =
    getPowerTransformerEndsVoltageSummary(calculationTactic, scenariosIds);

  const energyConsumersLoadsSummary = getEnergyConsumerLoadsSummary(
    calculationTactic,
    scenariosIds
  );

  const attentionRequired = isAttentionRequired(
    acLineSegmentEvaluationResult,
    energyConsumerEvaluationResult,
    transformerEvaluationResult
  );

  return {
    energyConsumerVoltages: energyConsumerEvaluationResult.voltageResult,
    acLineLoading: acLineSegmentEvaluationResult.loadingResult,
    transformerLoading: powerTransformers.loadingPercent,
    transformerAvailableCapacity: powerTransformers.availableCapacity,
    transformerRatedCapacity: transformerEvaluationResult.ratedCapacity,
    energyConsumerVoltageDiffTransformerEndPercent:
      energyConsumerEvaluationResult.voltageDiffTransformerEndPercentResult,
    energyConsumerVoltageDiffBetweenTransformer:
      energyConsumerEvaluationResult.voltageDiffTransformerEndResult,
    energyConsumerVoltageCosinePhi:
      energyConsumerEvaluationResult.voltageCosinePhiResult,
    energyConsumerVoltageNominal:
      energyConsumerEvaluationResult.voltageNominalResult,
    powerTransformerEndsVoltageSummary,
    energyConsumersLoadsSummary,
    attentionRequired
  };
}

function isAttentionRequired(
  acLineSegmentEvaluationResult: AcLineSegmentEvaluationResult,
  energyConsumerEvaluationResult: EnergyConsumerEvaluationResult,
  transformerEvaluationResult: HighestLoadingPowerTransformerEvaluationResult
): boolean {
  return (
    acLineSegmentEvaluationResult.attentionRequired ||
    energyConsumerEvaluationResult.attentionRequired ||
    transformerEvaluationResult.attentionRequired
  );
}
